import { useMemo, useState, useSyncExternalStore } from 'react';
import { RotateCcw, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { getKeybindingLayoutMap } from '../dispatch/effectiveKeymap';
import { keybindingCommandIds } from '../model/defaultKeybindings';
import { platform as currentPlatform } from '../model/platform';
import { resolveKeymap } from '../model/resolveKeymap';
import type { KeybindingCommandId, KeybindingNotice, SchemeId } from '../model/keybindingTypes';
import { availableKeybindingSchemes, schemeFor } from '../store/keybindingSchemes';
import { useKeybindingsStore } from '../store/keybindingsStore';
import { isCustomised } from '../store/keybindingsSelectors';
import { isKnownCommandId } from '../store/normalizeOverrides';
import { KeybindingNoticeList } from './KeybindingNoticeList';
import { KeybindingRow, type KeybindingRowWarning } from './KeybindingRow';
import { KeybindingsFooter } from './KeybindingsFooter';
import { isShownWarning, matchesKeybindingSearch, noticeText, type NoticeTextContext } from './keybindingDisplay';
import { groupCommandsByScope, SCOPE_TITLES } from './keybindingScopes';
import { SchemePicker } from './SchemePicker';
import { useKeybindingEditActions, type ActiveRecording } from './useKeybindingEditActions';

/** Non-durable notices shown under a row: its own, plus a scoped-vs-global pair it is part of. */
function rowWarnings(
  notices: readonly KeybindingNotice[],
  commandId: KeybindingCommandId,
  context: NoticeTextContext,
  scheme: SchemeId,
): KeybindingRowWarning[] {
  return notices
    .filter((notice) => !notice.persisted && (notice.commandId === commandId ||
      (notice.kind === 'scoped-global-overlap' && notice.relatedCommandId === commandId)))
    .filter((notice) => isShownWarning(notice, scheme, context.platform))
    .map((notice, index) => ({ key: `${notice.kind}:${notice.chord}:${index}`, text: noticeText(notice, context) }));
}

/** Settings → Keyboard Shortcuts (plan §3 "Settings dialog", Slice 2). */
export function KeyboardShortcutsSettingsTab() {
  const { track } = useAnalytics();
  const actions = useKeybindingEditActions();
  const scheme = useKeybindingsStore((state) => state.scheme);
  const overrides = useKeybindingsStore((state) => state.overrides);
  const keymap = useKeybindingsStore((state) => state.keymap);
  const notices = useKeybindingsStore((state) => state.notices);
  const layoutMap = getKeybindingLayoutMap();
  const layout = useSyncExternalStore(layoutMap.subscribe, layoutMap.getSnapshot, layoutMap.getSnapshot);
  const [query, setQuery] = useState('');
  const [recording, setRecording] = useState<ActiveRecording | null>(null);
  const platform = currentPlatform();

  // Stored chords (override or scheme) — a chip stays visible, struck through,
  // when validation leaves it inactive (e.g. a layout-dependent overlap).
  const storedChords = useMemo(
    () => resolveKeymap(schemeFor(scheme, availableKeybindingSchemes), overrides, platform),
    [overrides, platform, scheme],
  );
  const textContext: NoticeTextContext = { platform, isDev: import.meta.env.DEV, layout, keymap, overrides };
  const groups = groupCommandsByScope(keybindingCommandIds.filter((id) =>
    matchesKeybindingSearch(id, storedChords[id], query, platform, layout)));
  const hasKnownOverrides = Object.keys(overrides).some(isKnownCommandId);

  return (
    <div className="space-y-4" data-testid="keyboard-shortcuts-settings-tab">
      <p className="text-xs text-muted-foreground">
        Click a shortcut and press the new keys. A shortcut another command in the same area already uses moves to
        this command; the change is listed at the top with Undo.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <SchemePicker />
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            size="sm"
            className="pl-7"
            placeholder="Search actions or keys"
            aria-label="Search keyboard shortcuts"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            data-testid="keybindings-search"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!hasKnownOverrides}
          data-testid="keybindings-reset-all"
          onClick={() => {
            useKeybindingsStore.getState().resetAll();
            track('feature:keybindings_reset_all');
          }}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset all to scheme defaults
        </Button>
      </div>

      <KeybindingNoticeList textContext={textContext} />

      {groups.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="keybindings-no-results">
          No shortcuts match &ldquo;{query}&rdquo;.
        </p>
      )}
      {groups.map((group) => (
        <section key={group.scope} className="space-y-1.5" data-testid={`keybinding-group-${group.scope}`}>
          <h4 className="text-sm font-medium">{SCOPE_TITLES[group.scope]}</h4>
          <div className="divide-y divide-border rounded-md border border-border bg-background">
            {group.commandIds.map((commandId) => (
              <KeybindingRow
                key={commandId}
                commandId={commandId}
                chords={storedChords[commandId]}
                activeChords={keymap[commandId]}
                customised={isCustomised({ overrides }, commandId)}
                warnings={rowWarnings(notices, commandId, textContext, scheme)}
                recording={recording?.commandId === commandId ? recording.mode : null}
                onRecord={(mode) => setRecording((current) => {
                  if (mode) return { commandId, mode };
                  return current?.commandId === commandId ? null : current;
                })}
                actions={actions}
                platform={platform}
                layout={layout}
              />
            ))}
          </div>
        </section>
      ))}

      <KeybindingsFooter platform={platform} />
    </div>
  );
}
